import React, { useMemo, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { MemoLoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';

import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { Container } from '../Container';
import { useUserDiscard } from '../hooks/useUserDiscard';
import type { SessionUIComponentProps } from '../SessionUIComponent';
import type { Action } from '../useUIState';

export function LoadingSessionScreen({
	api,
	providerFactory,
	appearance,
	dispatch,
}: SessionUIComponentProps & { dispatch: React.Dispatch<Action> }): React.JSX.Element {
	const { isInDocumentStreamingEnabled } = useContentComponentContext();
	const schema = useMemo(() => api?.core.sharedState.currentState()?.schema, [api]);
	const { formatMessage } = useIntl();
	const { mounted: isRovoMounted, streaming: isRovoStreaming } = useUIStore();
	const onDiscard = useCallback(() => {
		if (!fg('platform_editor_ai_migrate_from_xstate')) {
			api?.aiExperience.actions.getAiExperienceActor()?.send({ type: 'enable discard' });
		} else {
			api?.core?.actions.execute(resetSessionState());
		}
		dispatch({ type: 'discard' });
	}, [api, dispatch]);

	const loadingScreenRef = React.useRef<HTMLDivElement | null>(null);
	useUserDiscard({ onDiscard, ref: loadingScreenRef });

	const hideLoadingScreen = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) =>
			aiExperienceState?.aiDecorationExperiencePluginState.hideLoadingScreen,
	);

	return (
		<Container ref={loadingScreenRef}>
			{schema && (
				<MemoLoadingScreen
					onCancel={onDiscard}
					preview={undefined}
					title={formatMessage(aiExperienceApplicationMessages.loadingTitle)}
					providerFactory={providerFactory}
					schema={schema}
					isStreaming
					canInlineStreamChanges
					hideLoadingScreen={(isRovoMounted && isRovoStreaming) || hideLoadingScreen}
					appearance={appearance}
					isAIFC={isInDocumentStreamingEnabled}
				/>
			)}
		</Container>
	);
}
